import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Box, VStack, HStack, Text } from '@chakra-ui/react'
import { FiFile, FiFileText } from 'react-icons/fi'
import { SearchResultItem } from '@/types'
import { useAnalytics } from '@/hooks/useAnalyticsWithUser'
import { useAppTranslation } from '@/contexts/LanguageContext'
import SearchResultCardSubtitle from './SearchResultCardSubtitle'

interface SearchResultCardProps {
  result: SearchResultItem
  searchTerm: string
  showSummary?: boolean
}

// Highlights each query word (3+ chars) in the text, case-insensitively
const highlightSearchTerm = (text: string, searchTerm: string) => {
  const words = searchTerm
    .split(/\s+/)
    .filter((word) => word.length >= 3)
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  if (words.length === 0) return text

  // With a capture group, split puts the matches at the odd indexes
  return text.split(new RegExp(`(${words.join('|')})`, 'gi')).map((part, index) =>
    index % 2 === 1 ? (
      <Box as="mark" key={index} bg="yellow.200" px={0.5} borderRadius="sm">
        {part}
      </Box>
    ) : (
      part
    )
  )
}

export default function SearchResultCard({
  result,
  searchTerm,
  showSummary = false,
}: SearchResultCardProps) {
  const { trackEvent } = useAnalytics()
  const location = useLocation()
  const { t, currentLanguage } = useAppTranslation('search')

  // Same preference as the preview sidebar: the UI language, then English, then any
  const summaries = result.summaries ?? []
  const summary = (
    summaries.find((s) => s.language === currentLanguage) ??
    summaries.find((s) => s.language === 'en') ??
    summaries[0]
  )?.summary
  const fileName = result.file_name || result.original_name

  const handleResultClick = () => {
    // Track search result click
    trackEvent('search_result_clicked', {
      file_id: result.file_id,
      file_name: result.file_name || result.original_name,
      file_type: result.mime_type,
      search_term: searchTerm.toLowerCase(),
      score: result.score || 0,
    })
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return <FiFile color="#e53e3e" />
    }
    return <FiFileText />
  }

  const locationParams = new URLSearchParams(location.search)
  const currentQuery = locationParams.get('q') || locationParams.get('query') || ''
  const previewUrl = `/preview/${result.file_id}?from=search&q=${encodeURIComponent(currentQuery)}`
  // Full search URL (with filters) so Back lands on the exact same cached results
  const previewState = { searchUrl: location.pathname + location.search }

  return (
    <Box
      as={Link}
      to={previewUrl}
      state={previewState}
      onClick={handleResultClick}
      p={6}
      bg="white"
      borderRadius="xl"
      border="1px"
      borderColor="gray.200"
      cursor="pointer"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'md',
        transform: 'translateY(-1px)',
        textDecoration: 'none',
      }}
      transition="all 0.2s ease"
      display="block"
      minW={0}
      overflow="hidden"
    >
      <VStack align="start" spacing={3}>
        {/* Header with icon and filename */}
        <HStack spacing={3} w="full">
          <Box fontSize="xl" color="blue.600" flexShrink={0}>
            {getFileIcon(result.mime_type)}
          </Box>
          <VStack align="start" spacing={1} flex={1} minW={0}>
            <Text
              fontWeight="semibold"
              fontSize="lg"
              color="gray.900"
              noOfLines={1}
              wordBreak="break-all"
              lineHeight="short"
              title={fileName}
            >
              {fileName}
            </Text>
            <SearchResultCardSubtitle result={result} />
          </VStack>
        </HStack>

        {/* Summary, or the passage that matched */}
        {showSummary && summary ? (
          <Text
            fontSize="sm"
            color="gray.700"
            lineHeight="relaxed"
            noOfLines={4}
            className="ph-no-capture file-content-private"
            data-private="true"
          >
            {summary}
          </Text>
        ) : (
          <VStack align="start" spacing={1} w="full">
            {showSummary && (
              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                {t('results.noSummary')}
              </Text>
            )}
            <Text
              fontSize="sm"
              color="gray.600"
              lineHeight="relaxed"
              noOfLines={3}
              className="ph-no-capture file-content-private"
              data-private="true"
            >
              {highlightSearchTerm(result.text, searchTerm)}
            </Text>
          </VStack>
        )}
      </VStack>
    </Box>
  )
}
